import { Button, Skeleton } from '@mantine/core';
import { IconArrowsExchange, IconLogin, IconLogout } from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDay } from '../../format.js';
import classes from './CardMovements.module.css';
import {
  type DrawerMovement,
  drawerAssignments,
  drawerCanSignOn,
  drawerRowActions,
  movementTarget,
} from './card-movements.js';
import { RankChangeDialog, SignOffDialog, SignOnDialog } from './MovementDialogs.js';
import { errorCode, type PersonnelSource } from './source.js';
import type { CrewAssignment, CrewProfileView } from './types.js';

const ICONS = { signOff: IconLogout, rankChange: IconArrowsExchange } as const;

/**
 * Kartlı kişinin gemi atamaları ve işlemleri (görünüm; ADR 0155 Ek, sahip 2026-11-02 gece): her
 * satırda uygun olan Sign off ve Rank change, kişi gemide değilse Sign on. Kurallar Crew Profile
 * ile aynıdır (`card-movements.ts`).
 */
export function CardMovementsView({
  view,
  onSignOn,
  onMovement,
}: {
  view: CrewProfileView;
  onSignOn?: (() => void) | undefined;
  onMovement?: ((action: DrawerMovement, row: CrewAssignment) => void) | undefined;
}) {
  const { t } = useTranslation();
  const m = (key: string, options: Record<string, unknown> = {}) => t(`signOnOff.${key}`, options);
  const rows = drawerAssignments(view);
  return (
    <div className={classes.root}>
      {rows.length === 0 ? (
        <p className={classes.muted}>{t('setup.users.drawer.noAssignments')}</p>
      ) : (
        <ul className={classes.rows}>
          {rows.map((row) => {
            const actions = onMovement ? drawerRowActions(view, row) : [];
            const text = `${row.vesselName} · ${row.rank ?? '—'}`;
            return (
              <li key={row.id} className={classes.row}>
                <span className={classes.what}>
                  <span className={classes.name}>{text}</span>
                  <span className={classes.muted}>
                    {formatDay(row.validFrom)} – {row.validTo ? formatDay(row.validTo) : '…'}
                  </span>
                </span>
                {actions.length > 0 ? (
                  <span className={classes.actions}>
                    {actions.map(({ action, hint }) => {
                      const Icon = ICONS[action];
                      return (
                        <Button
                          key={action}
                          size="compact-xs"
                          variant="default"
                          leftSection={<Icon size={14} />}
                          disabled={hint !== null}
                          title={hint ? m(`action.${hint}`) : undefined}
                          aria-label={`${m(`action.${action}`)}: ${text}`}
                          onClick={() => onMovement?.(action, row)}
                        >
                          {m(`action.${action}`)}
                        </Button>
                      );
                    })}
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {onSignOn && drawerCanSignOn(view) ? (
        <Button size="xs" leftSection={<IconLogin size={14} />} onClick={onSignOn}>
          {t('crewProfile.signOn')}
        </Button>
      ) : null}
    </div>
  );
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; code: string }
  | { kind: 'ready'; view: CrewProfileView };

/**
 * Settings ▸ Users çekmecesinde kartlı kişinin gemi rolleri (ADR 0155 Ek): kartı okur
 * (`crewProfile`), işlemleri Sign On / Off'un pencereleriyle ve aynı uçlarla yazar. MLC çalışma
 * kaydı (`crew_movement_events`), denetim kaydı, kurallar ve iyimser kilit ofisin Sign On / Off'uyla
 * aynıdır. Her işlemden sonra kart yeniden okunur ve `onChanged` çağrılır (kişi satırının sürümü
 * değişir; ADR 0144 §1).
 */
export function CardMovements({
  source,
  userId,
  onChanged,
}: {
  source: PersonnelSource;
  userId: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const editor = source.scope === 'web' ? source.movementEditor : undefined;
  const [state, setState] = useState<State>({ kind: 'loading' });
  const [movement, setMovement] = useState<
    { action: 'signOn' } | { action: DrawerMovement; row: CrewAssignment } | null
  >(null);
  const [vessels, setVessels] = useState<{ value: string; label: string }[]>([]);

  const load = useCallback(() => {
    source
      .crewProfile(userId)
      .then((view) => setState({ kind: 'ready', view }))
      .catch((error: unknown) => setState({ kind: 'error', code: errorCode(error) }));
  }, [source, userId]);
  useEffect(load, [load]);

  if (state.kind === 'loading') return <Skeleton height={56} radius="sm" />;
  if (state.kind === 'error') {
    return (
      <p className={classes.error} role="alert">
        {t('setup.users.drawer.crewLoadError', { code: state.code })}{' '}
        <Button size="compact-xs" variant="subtle" onClick={load}>
          {t('common.retry')}
        </Button>
      </p>
    );
  }
  const { view } = state;
  const moved = () => {
    setMovement(null);
    load();
    onChanged();
  };
  const signOn = () => {
    setMovement({ action: 'signOn' });
    source
      .vessels()
      .then((list) => setVessels(list.map((vessel) => ({ value: vessel.id, label: vessel.name }))))
      .catch(() => setVessels([]));
  };
  const target =
    movement && movement.action !== 'signOn' ? movementTarget(view, movement.row, 'joining') : null;
  const today = view.today ?? new Date().toISOString().slice(0, 10);
  return (
    <>
      <CardMovementsView
        view={view}
        onSignOn={editor ? signOn : undefined}
        onMovement={editor ? (action, row) => setMovement({ action, row }) : undefined}
      />
      {editor && movement?.action === 'signOn' && vessels.length > 0 ? (
        <SignOnDialog
          editor={editor}
          people={[{ value: view.userId, label: view.fullName }]}
          userId={view.userId}
          vessels={vessels}
          canSetWage={view.canSetWage === true}
          onClose={() => setMovement(null)}
          onSaved={moved}
        />
      ) : null}
      {editor && target && movement?.action === 'signOff' ? (
        <SignOffDialog
          editor={editor}
          target={target}
          onClose={() => setMovement(null)}
          onSaved={moved}
        />
      ) : null}
      {editor && target && movement?.action === 'rankChange' ? (
        <RankChangeDialog
          editor={editor}
          target={target}
          today={today}
          onClose={() => setMovement(null)}
          onSaved={moved}
        />
      ) : null}
    </>
  );
}
